/**
 * AssemblyInspectorPanel — focused side panel for a single hierarchical
 * assembly output (XML / JSON byte file).
 *
 * Replaces the generic "browse all outputs" drawer for assembly nodes.
 * Shows the one file the user cares about, the primary Download action,
 * and the inline preview.  Intentionally does NOT reuse the mapped-tables
 * drawer — clicking a specific node should show that node's data, not
 * the whole world.
 */
import React from 'react';
import { Box, Chip, IconButton, Stack, Typography, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as ExportIcon,
  AccountTree as AssemblyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import HierarchicalPreview from './HierarchicalPreview';
import { assembleOutput, downloadAssemblyOutput } from '../../services/workspaceApi';
import {
  fetchReadiness,
  resolvePostMappingFindings,
} from '../../services/readinessApi';
import type { ReadinessResolutionRequest } from '../../types/readiness';
import { useNotifications } from '../../context/NotificationContext';
import ReadinessPanel from './readiness/ReadinessPanel';
import { useResolveAutoRefresh } from './readiness/useResolveAutoRefresh';
import {
  ReadinessChip,
  chipPropsFromFindings,
} from './readiness/ReadinessChip';
import { useReadinessDeepLink } from './readiness/useReadinessDeepLink';
import { aggregateByRootCause } from '../../types/readiness';
import type {
  ReadinessFinding,
  ReadinessStatus,
} from '../../types/readiness';
import type { PipelineNode } from './DAGCanvas';

interface AssemblyInspectorPanelProps {
  sessionId: string;
  selectedNode: PipelineNode;
  onClose: () => void;
  /** Optional: open the connector picker in assembly mode. */
  onExportToConnector?: () => void;
}

const formatBytes = (n?: number): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#94a3b8',
  running: '#a855f7',
  completed: '#22c55e',
  failed: '#ef4444',
};

const AssemblyInspectorPanel: React.FC<AssemblyInspectorPanelProps> = ({
  sessionId,
  selectedNode,
  onClose,
  onExportToConnector,
}) => {
  const { showToast } = useNotifications();

  // Local override applied when the user clicks "Recompute" — the
  // on-demand /workspace/assemble endpoint writes a fresh output and
  // returns its ref, which we swap in here so the preview reads the
  // freshly computed bytes rather than the stale step output.
  const [recomputed, setRecomputed] = React.useState<{
    outputRef: string;
    byteSize: number;
    format: string;
    rowCount: number;
  } | null>(null);
  // Reset the override when the user switches to a different node.
  React.useEffect(() => {
    setRecomputed(null);
  }, [selectedNode.stepId]);

  const outputRef = recomputed?.outputRef || selectedNode.outputRef || '';
  const filename = outputRef.split('/').pop() || 'output';
  const format = recomputed?.format
    || selectedNode._assemblyOutputFormat
    || (outputRef.endsWith('.json') ? 'json' : 'xml');
  const byteSize = recomputed?.byteSize ?? selectedNode._assemblyByteSize;
  const rowCount = recomputed?.rowCount ?? selectedNode.rowCount;
  const status = selectedNode.status;
  const error = selectedNode._assemblyError;
  const accent = STATUS_COLORS[status] || '#94a3b8';

  const canDownload = !!outputRef && outputRef.startsWith('assembly://');

  // Manual preview-refresh counter. Bumped by the header refresh button so
  // HierarchicalPreview re-fetches the bytes at outputRef. Needed because a
  // pipeline rerun reuses the same step_id (and therefore the same outputRef),
  // so the URL itself doesn't change — only the bytes at that path do.
  const [previewRefreshKey, setPreviewRefreshKey] = React.useState(0);

  // Readiness rollout — fetch structured findings + status from the
  // new endpoint. The chip and panel render off this state; while
  // the request is in flight the chip shows "checking" so users see
  // the readiness state is being recomputed.
  const [findings, setFindings] = React.useState<ReadinessFinding[]>([]);
  const [readinessStatus, setReadinessStatus] =
    React.useState<ReadinessStatus | null>(null);
  const [readinessMessage, setReadinessMessage] = React.useState<
    string | undefined
  >(undefined);
  const [readinessLoading, setReadinessLoading] = React.useState(false);

  // Stable refresh fn so initial fetch + stale-on-focus share the path.
  const refreshReadiness = React.useCallback(() => {
    if (!sessionId) return () => {};
    let cancelled = false;
    setReadinessLoading(true);
    fetchReadiness({
      sessionId,
      consumerKind: 'hierarchical_assembly',
      nodeId: selectedNode.stepId,
    })
      .then((resp) => {
        if (cancelled) return;
        setFindings(resp.findings || []);
        setReadinessStatus(resp.status);
        setReadinessMessage(resp.message);
      })
      .catch(() => {
        if (cancelled) return;
        setFindings([]);
        setReadinessStatus(null);
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, selectedNode.stepId]);

  React.useEffect(() => {
    return refreshReadiness();
  }, [refreshReadiness, outputRef, status]);

  // Recompute the assembly on demand. Calls the same /workspace/assemble
  // endpoint that the "Download final XML" action uses — reads the current
  // __mapped_*__ views directly and writes fresh bytes — then swaps the
  // panel onto the new output_ref so the preview reflects the recomputed
  // result. The stale persisted step output remains untouched in the DB;
  // this is intentional, since the step's own outputRef is the
  // pipeline-execution artifact and we don't want a sidecar recompute to
  // rewrite history. A subsequent build_and_run_pipeline rerun will
  // overwrite the step output through the normal pipeline path.
  const [recomputing, setRecomputing] = React.useState(false);
  const handleRecompute = React.useCallback(async () => {
    if (!sessionId || recomputing) return;
    setRecomputing(true);
    try {
      const result = await assembleOutput(sessionId);
      setRecomputed({
        outputRef: result.output_ref,
        byteSize: result.byte_size,
        format: result.format,
        rowCount: result.row_count,
      });
      setPreviewRefreshKey((n) => n + 1);
      refreshReadiness();
      const warningSummary = result.warnings?.length
        ? ` (${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''})`
        : '';
      showToast(
        'Assembly recomputed',
        `${result.row_count.toLocaleString()} instances, ${(result.byte_size / 1024).toFixed(1)} KB${warningSummary}`,
        'success',
      );
    } catch (e) {
      showToast(
        'Recompute failed',
        e instanceof Error ? e.message : 'Could not recompute the assembly.',
        'error',
      );
    } finally {
      setRecomputing(false);
    }
  }, [sessionId, recomputing, refreshReadiness, showToast]);

  // Plan §"Stale readiness" — re-fetch when the user returns to the
  // tab/panel after editing the mapping elsewhere.
  React.useEffect(() => {
    const onFocus = () => refreshReadiness();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshReadiness();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshReadiness]);

  // Aggregate raw findings by root cause so the chip + panel header +
  // Issues-heading all show the same count. Decorate each unique
  // finding's user_detail with the affected-field count so a single
  // card visibly represents many raw findings.
  const aggregatedForChip = React.useMemo(() => {
    const groups = new Map<
      string,
      { sample: ReadinessFinding; count: number }
    >();
    for (const f of findings) {
      const obs = (f.observed as Record<string, unknown>) || {};
      const k =
        `${f.code}|${obs.source_table || ''}|${obs.source_column || ''}`;
      const existing = groups.get(k);
      if (existing) existing.count += 1;
      else groups.set(k, { sample: f, count: 1 });
    }
    return Array.from(groups.values()).map(({ sample, count }) =>
      count > 1
        ? {
            ...sample,
            user_detail: `${sample.user_detail} (Affects ${count} fields.)`,
          }
        : sample,
    );
  }, [findings]);
  const chipDerived = chipPropsFromFindings(aggregatedForChip);
  const chipState = readinessLoading
    ? 'checking'
    : findings.length === 0 && !readinessStatus
    ? 'empty'
    : chipDerived.state;

  // Plan §"One button per issue" — wire ``Fix in mapping →`` / ``Review
  // data →`` / ``Adjust schema →`` actions through a single deep-link
  // router. Without this, every finding card's button was a no-op.
  const { handlePrimaryAction } = useReadinessDeepLink();

  // Inline resolution submitter — Phase 1 of post-mapping resolution
  // surface. When a finding carries ``resolution_options`` + a
  // ``finding_id`` (currently: operator_value_required PD projections
  // and PLAN_CONTRACT_VIOLATION assembly findings), the card renders
  // ReadinessResolutionForm and the user picks an option inline. The
  // resolve endpoint writes the choice into workspace_meta — engine
  // mode picks it up at the next materialize, iterative mode picks
  // it up as an agent hint on the next turn. Re-fetch readiness after
  // every submit so the panel reflects newly-resolved findings.
  // Subscribe to the SSE rebroadcast: when the orchestrator finishes
  // any bg dispatch for this session, refresh readiness. Replaces the
  // earlier polling burst — the platform already maintains one SSE
  // stream per agent session via useWorkspaceSession; we just react
  // to the rebroadcast window event.
  useResolveAutoRefresh(sessionId, refreshReadiness);

  const handleResolve = React.useCallback(
    async (req: ReadinessResolutionRequest) => {
      const resp = await resolvePostMappingFindings(sessionId, [req]);
      if (resp.errors && resp.errors.length > 0) {
        const first = resp.errors[0];
        throw new Error(first?.reason || 'Could not apply your choice.');
      }
      showToast(
        'Saved',
        resp.next_step || 'Your choice has been recorded.',
        'success',
      );
      refreshReadiness();
      // SSE drives the next refresh — no client-side polling needed.
      return { dispatched: resp.dispatched || {}, next_step: resp.next_step };
    },
    [sessionId, showToast, refreshReadiness],
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header — compact single-row chrome. The filename, format,
          byte size, and row count are inline as chips; the primary
          Download lives next to the Tree/Raw tabs inside HierarchicalPreview
          (was duplicated here in the legacy primary-actions row). */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${accent}` }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <AssemblyIcon sx={{ fontSize: 18, color: accent }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Hierarchical Assembly
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              flex: 1,
              fontFamily: '"Fira Code", monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={filename}
          >
            {filename}
          </Typography>
          {/* Readiness chip — single source of "is this ready?" truth.
              Replaces the separate "Completed" status chip; the lifecycle
              is implied (Ready ⇒ completed). */}
          <ReadinessChip state={chipState} count={chipDerived.count} />
          <Tooltip title={recomputing ? 'Recomputing assembly…' : 'Recompute assembly'}>
            <span>
              <IconButton
                size="small"
                disabled={recomputing}
                onClick={handleRecompute}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={recomputing ? {
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                  } : undefined}
                />
              </IconButton>
            </span>
          </Tooltip>
          {onExportToConnector && (
            <Tooltip title="Export to connector">
              <span>
                <IconButton
                  size="small"
                  disabled={!canDownload || readinessStatus === 'not_ready'}
                  onClick={onExportToConnector}
                >
                  <ExportIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label={format.toUpperCase()} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
          {byteSize != null && (
            <Chip label={formatBytes(byteSize)} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
          )}
          {rowCount != null && (
            <Chip label={`${rowCount.toLocaleString()} rows`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
          )}
        </Stack>
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Readiness panel — universal findings list. The preview is
          slotted in below as the consumer-specific child. */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {(findings.length > 0 || readinessStatus || readinessMessage) && (
          <ReadinessPanel
            consumerKind="hierarchical_assembly"
            findings={aggregatedForChip}
            status={readinessStatus ?? undefined}
            message={readinessMessage}
            onPrimaryAction={handlePrimaryAction}
            onResolve={handleResolve}
            previewSlot={
              canDownload ? (
                <HierarchicalPreview
                  sessionId={sessionId}
                  outputRef={outputRef}
                  outputFormat={format}
                  byteSize={byteSize}
                  refreshKey={previewRefreshKey}
                  onDownload={() =>
                    downloadAssemblyOutput(sessionId, outputRef).catch((err) => {
                      showToast('Download failed', err?.message || 'Failed to download assembly output', 'error');
                    })
                  }
                  findings={aggregatedForChip.map((f) => ({
                    code: f.code,
                    severity: f.severity,
                    target_path: f.target_path,
                    target_element: f.target_element,
                    user_title: f.user_title,
                  }))}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Preview will appear once the assembly step completes.
                </Typography>
              )
            }
          />
        )}
        {findings.length === 0 && !readinessStatus && !readinessMessage && (
          <Box sx={{ px: 2, py: 1.5 }}>
            {canDownload ? (
              <HierarchicalPreview
                sessionId={sessionId}
                outputRef={outputRef}
                outputFormat={format}
                byteSize={byteSize}
                refreshKey={previewRefreshKey}
                onDownload={() =>
                  downloadAssemblyOutput(sessionId, outputRef).catch((err) => {
                    showToast('Download failed', err?.message || 'Failed to download assembly output', 'error');
                  })
                }
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Preview will appear once the assembly step completes.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssemblyInspectorPanel;
